import {
  Directive,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewContainerRef,
  inject,
  HostAttributeToken,
  input,
} from '@angular/core';
import { debounceTime, distinctUntilChanged, map, skip, startWith, Subject, Subscription } from 'rxjs';

/**
 * @description für Anzeige/Ausblendung abhängig von element;
 * @hides host, wenn mit `*` benutzt;
 * @returns true/false durch template variable (query = directive) q.matches
 * @example *el-media-query="{min: 1000, max: 1200, el: elref}
 * @example [el-media-query]="{max: 600, el: elref}" #q="query" [clrInMenu]="q.matches"
 */
@Directive({
  selector: '[el-media-query]',
  exportAs: 'query',
})
export class ElementMediaQueryDirective implements OnInit, OnDestroy {
  private readonly viewContainerRef = inject(ViewContainerRef, {
    optional: true,
  });
  private readonly templateRef = inject<TemplateRef<unknown>>(TemplateRef, {
    optional: true,
  });
  private readonly dimension = inject(new HostAttributeToken('media_height'), {
    optional: true,
  });
  private readonly timeout = inject(new HostAttributeToken('debounce_time'), {
    optional: true,
  });

  readonly mode = input.required<{
    min?: number;
    max?: number;
    el: HTMLElement;
  }>({ alias: 'el-media-query' });

  private resizer: ResizeObserver;
  private resized$ = new Subject<ResizeObserverEntry[]>();
  private sub: Subscription;

  public matches: boolean = false;

  private readonly box_size: 'blockSize' | 'inlineSize';
  constructor() {
    this.dimension = this.dimension !== null ? 'height' : 'width';
    this.box_size = this.dimension == 'height' ? 'blockSize' : 'inlineSize';
    this.timeout ||= '200';
  }

  ngOnInit() {
    const mode = this.mode();
    if (!mode || !(mode.min || mode.max) || !mode.el) return;

    this.resizer = new ResizeObserver((entries: ResizeObserverEntry[]) => this.resized$.next(entries));

    this.resizer.observe(mode.el);

    this.sub = this.resized$
      .pipe(
        debounceTime(+this.timeout),
        map(entries => {
          const size = entries[0].borderBoxSize[0][this.box_size];
          const modeValue = this.mode();
          return (!modeValue.max || size <= modeValue.max) && (!modeValue.min || size >= modeValue.min);
        }),
        startWith(false),
        distinctUntilChanged(),
        skip(1)
      )
      .subscribe(matches => this.updateView(matches));
  }

  private updateView = (matches: boolean) => {
    this.matches = matches;
    if (matches && this.templateRef) {
      const ref = this.viewContainerRef?.createEmbeddedView(this.templateRef);
      ref?.markForCheck();
    } else this.viewContainerRef?.clear();
  };

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }
}
